import { DateTime, Duration } from 'luxon';

import {
	Appointment,
	AppointmentAvailabilityParams,
	AppointmentType,
	CareService,
	Encounter,
	EncounterStatus,
	ListAppointmentsParams,
	ListProvidersParams,
	LocationType,
	Provider,
	ProviderAvailability,
	RescheduleAppointmentParams,
	ReserveAppointmentParams,
} from './dtos';
import { APIError } from './APIError';

export class Client {
	constructor(
		private readonly authToken: string,
		private readonly api: string = process.env.GATSBY_FOLX_API_V2_URL ??
			'https://apiv2.integration.folxhealth.net',
		private readonly headers: Record<string, string> = {}
	) {
		if (api.endsWith('/')) {
			this.api = api.replace(/([^/])\/+$/g, '$1');
		}

		this.headers['authorizationToken'] = this.authToken;
		this.headers['Content-Type'] = 'application/json';
		this.headers['Accept'] = 'application/json';
	}

	async *listAppointments(
		params?: ListAppointmentsParams,
		abort?: AbortSignal
	): AsyncGenerator<Appointment> {
		const queryParams = new URLSearchParams({
			...(params?.limit && { limit: params.limit.toString() }),
			...(params?.providerID && { provider_id: params.providerID }),
			...(params?.statusIs && { status_is: params.statusIs }),
			...(params?.statusIsNot && { status_is_not: params.statusIsNot }),
			...(params?.locationType && { location_type: params.locationType }),
			...(params?.scheduledFrom && { scheduled_from: params.scheduledFrom }),
			...(params?.scheduledTo && { scheduled_to: params.scheduledTo }),
			...(params?.includeCanceled && { include_canceled: 'true' }),
			...(params?.includeUnscheduled && { include_unscheduled: 'true' }),
			...(params?.ascending && { ascending: 'true' }),
		});

		let nextPageToken: string | undefined;

		do {
			if (nextPageToken !== undefined) {
				queryParams.set('page_token', nextPageToken);
			}

			type Response = {
				next_page_token?: string;
				items?: AppointmentDTO[];
			};

			const resp = await this.send<Response>('GET', '/api/v1/scheduling/appointments', {
				params: queryParams,
				abort,
			});

			for (const a of resp.items ?? []) {
				yield mapAppointmentFromDTO(a);
			}

			nextPageToken = resp.next_page_token;
		} while (nextPageToken !== undefined);
	}

	async getAppointment(appointmentID: string, abort?: AbortSignal): Promise<Appointment> {
		const appt = await this.send<AppointmentDTO>(
			'GET',
			`/api/v1/scheduling/appointments/${appointmentID}`,
			{ abort }
		);

		return mapAppointmentFromDTO(appt);
	}

	async getNextEncounter(appointmentID: string, abort?: AbortSignal): Promise<Encounter> {
		const encounter = await this.send<EncounterDTO>(
			'GET',
			`/api/v1/scheduling/appointments/${appointmentID}/encounters/next`,
			{ abort }
		);

		return mapEncounterFromDTO(encounter);
	}

	async getAppointmentType(
		appointmentTypeID: string,
		abort?: AbortSignal
	): Promise<AppointmentType> {
		const apptType = await this.send<AppointmentTypeDTO>(
			'GET',
			`/api/v1/appointmenttypes/${appointmentTypeID}`,
			{ abort }
		);

		return mapAppointmentTypeFromDTO(apptType);
	}

	async getAppointmentTypeForMember(
		careServiceID: string,
		memberID?: string,
		abort?: AbortSignal
	): Promise<AppointmentType> {
		const params = {
			...(memberID && { member_id: memberID }),
		};

		const apptType = await this.send<AppointmentTypeDTO>(
			'GET',
			`/api/v1/scheduling/appointment-type-for/${careServiceID}`,
			{ params, abort }
		);

		return mapAppointmentTypeFromDTO(apptType);
	}

	async *listProviders(params: ListProvidersParams, abort?: AbortSignal): AsyncGenerator<Provider> {
		const queryParams = new URLSearchParams({
			...(params.limit && { limit: params.limit.toString() }),
			...(params.includeDeleted && { include_deleted: 'true' }),
			...(params.providesCareServiceID && {
				provides_care_service_id: params.providesCareServiceID,
			}),
			...(params.canForState && { can_for_state: params.canForState }),
			...(params.canForMember && { can_for_member: params.canForMember }),
		});

		let nextPageToken: string | undefined;

		do {
			if (nextPageToken !== undefined) {
				queryParams.set('page_token', nextPageToken);
			}

			type Response = {
				next_page_token?: string;
				items?: ProviderDTO[];
			};
			const resp = await this.send<Response>('GET', '/api/v1/providers', {
				params: queryParams,
				abort,
			});

			for (const p of resp.items ?? []) {
				yield mapProviderDTO(p);
			}

			nextPageToken = resp.next_page_token;
		} while (nextPageToken !== undefined);
	}

	async getProvider(providerID: string, abort?: AbortSignal): Promise<Provider> {
		const resp = await this.send<ProviderDTO>('GET', `/api/v1/providers/${providerID}`, { abort });

		return mapProviderDTO(resp);
	}

	async getAppointmentTypeAvailability(
		params: AppointmentAvailabilityParams,
		abort?: AbortSignal
	): Promise<ProviderAvailability[]> {
		type ProviderAvailability =
			| {
					provider_id: string;
					times: string[];
			  }
			| {
					provider_id: string;
					next: string;
			  };
		type Response = {
			appointment_type_id: string;
			providers: ProviderAvailability[];
		};

		const resp = await this.send<Response>('GET', '/api/v1/scheduling/availability', {
			params: {
				appointment_type_id: params.appointmentTypeID,
				...(params.from && { from: params.from?.toISO() }),
				...(params.to && { to: params.to?.toISO() }),
				...(params.memberID && { member_id: params.memberID }),
				...(params.providerID && { provider_id: params.providerID }),
			},
			abort,
		});

		return resp.providers.map((pa) =>
			'times' in pa
				? {
						providerID: pa.provider_id,
						times: pa.times.map((t) => DateTime.fromISO(t)),
				  }
				: {
						providerID: pa.provider_id,
						next: DateTime.fromISO(pa.next),
				  }
		);
	}

	async reserveAppointment(
		params: ReserveAppointmentParams,
		abort?: AbortSignal
	): Promise<Appointment> {
		const resp = await this.send<AppointmentDTO>('POST', '/api/v1/scheduling/appointments', {
			body: {
				appointment_type_id: params.appointmentTypeID,
				when: params.when.toUTC().toISO(),
				timezone: params.when.zoneName,
				provider_id: params.providerID,
				sms_reminder_opt_in: params.smsReminderOptIn,
				...(params.memberID && { member_id: params.memberID }),
				...(params.reservationID && { reservation_id: params.reservationID }),
				...(params.phoneNumber && { phone_number: params.phoneNumber }),
			},
			abort,
		});

		return mapAppointmentFromDTO(resp);
	}

	async rescheduleAppointment(
		params: RescheduleAppointmentParams,
		abort?: AbortSignal
	): Promise<Encounter> {
		const resp = await this.send<EncounterDTO>(
			'PUT',
			`/api/v1/scheduling/appointments/${params.appointmentID}`,
			{
				body: {
					when: params.when.toUTC().toISO(),
					timezone: params.when.zoneName,
					provider_id: params.providerID,
					...(params.memberID && { member_id: params.memberID }),
					...(params.reservationID && { reservation_id: params.reservationID }),
				},
				abort,
			}
		);

		return mapEncounterFromDTO(resp);
	}

	async cancelAppointment(appointmentID: string, abort?: AbortSignal): Promise<void> {
		await this.send('DELETE', `/api/v1/scheduling/appointments/${appointmentID}`, { abort });
	}

	async confirmAppointment(appointmentID: string, abort?: AbortSignal): Promise<Encounter> {
		const resp = await this.send<EncounterDTO>(
			'POST',
			`/api/v1/scheduling/appointments/${appointmentID}/encounters`,
			{ abort }
		);

		return mapEncounterFromDTO(resp);
	}

	async startChatConversation(abort?: AbortSignal): Promise<string> {
		const resp = await this.send<{ conversation_id: string }>(
			'POST',
			'/api/v1/chat/conversations',
			{ body: {}, abort }
		);

		return resp.conversation_id;
	}

	async sendChatMessage(
		conversationID: string,
		message: string,
		abort?: AbortSignal
	): Promise<string> {
		const resp = await this.send<{ reply: string }>(
			'POST',
			`/api/v1/chat/conversations/${conversationID}/messages`,
			{ body: { message }, abort }
		);

		return resp.reply;
	}

	async listCareServices(abort?: AbortSignal): Promise<CareService[]> {
		type Response = {
			has_more: boolean;
			items: CareServiceDTO[];
		};

		const resp = await this.send<Response>('GET', '/api/v1/careservices', { abort });
		const careServices = resp.items.map(mapCareServiceFromDTO);

		return careServices;
	}

	private async send(
		method: 'DELETE',
		endpoint: string,
		options?: Omit<RequestOptions, 'body'>
	): Promise<void>;
	private async send<T extends object>(
		method: 'GET',
		endpoint: string,
		options?: Omit<RequestOptions, 'body'>
	): Promise<T>;
	private async send<T extends object>(
		method: 'POST' | 'PUT',
		endpoint: string,
		options?: RequestOptions
	): Promise<T>;

	private async send<T extends object | void>(
		method: Method,
		endpoint: string,
		options?: RequestOptions
	): Promise<T | undefined> {
		if (options?.params !== undefined && !(options.params instanceof URLSearchParams)) {
			options.params = new URLSearchParams(options.params);
		}

		const url = this.api + endpoint + '?' + (options?.params?.toString() ?? '');

		const init: RequestInit = {
			method: method,
			headers: this.headers,
			signal: options?.abort,
		};

		if (options && 'body' in options) {
			init.body = JSON.stringify(options.body);
		}

		const resp = await fetch(url, init);

		if (!resp.ok) {
			throw new APIError(resp.status, await resp.text());
		}

		if (resp.status === 204) {
			return;
		}

		return resp.json() as T;
	}
}

// Only need a subset at this time.
type Method = 'DELETE' | 'GET' | 'POST' | 'PUT';

type RequestOptions = {
	params?: URLSearchParams | Record<string, string>;
	body?: unknown;
	abort?: AbortSignal;
};

type AppointmentDTO = {
	id: string;
	created_at: string;
	updated_at: string;
	deleted_at?: string;
	member_id: string;
	appointment_type_id: string;
	scheduled_for: string;
	timezone: string;
	duration: number;
	provider_id: string;
	location_type?: string;
};

const mapAppointmentFromDTO = (dto: AppointmentDTO): Appointment => ({
	id: dto.id,
	createdAt: DateTime.fromISO(dto.created_at),
	updatedAt: DateTime.fromISO(dto.updated_at),
	deletedAt: dto.deleted_at ? DateTime.fromISO(dto.deleted_at) : undefined,
	memberID: dto.member_id,
	appointmentTypeID: dto.appointment_type_id,
	scheduledFor: DateTime.fromISO(dto.scheduled_for),
	timezone: dto.timezone,
	duration: Duration.fromObject({ minutes: dto.duration }),
	providerID: dto.provider_id,
	locationType: dto.location_type,
});

type AppointmentTypeDTO = {
	id: string;
	name: string;
	care_service_id: string;
	duration: number;
	is_virtual: boolean;
	scheduling_step: number;
	buffer_before: number;
	buffer_after: number;
	min_booking_advance: number;
	max_booking_advance: number;
};

const mapAppointmentTypeFromDTO = (dto: AppointmentTypeDTO): AppointmentType => ({
	id: dto.id,
	name: dto.name,
	careServiceID: dto.care_service_id,
	duration: Duration.fromObject({ minutes: dto.duration }),
	isVirtual: dto.is_virtual,
	schedulingStep: Duration.fromObject({ minutes: dto.scheduling_step }),
	bufferBefore: Duration.fromObject({ minutes: dto.buffer_before }),
	bufferAfter: Duration.fromObject({ minutes: dto.buffer_after }),
	minBookingAdvance: Duration.fromObject({ minutes: dto.min_booking_advance }),
	maxBookingAdvance: Duration.fromObject({ minutes: dto.max_booking_advance }),
});

type EncounterDTO = {
	id: string;
	appointment_id: string;
	provider_id: string;
	status: string;
	scheduled_for: string;
	location_type: string;
	location_id: string;
	location_value: string;
};

const mapEncounterFromDTO = (dto: EncounterDTO): Encounter => ({
	id: dto.id,
	appointmentID: dto.appointment_id,
	providerID: dto.provider_id,
	status: dto.status as EncounterStatus,
	scheduledFor: DateTime.fromISO(dto.scheduled_for),
	locationType: dto.location_type as LocationType,
	locationID: dto.location_id,
	locationValue: dto.location_value,
});

type ProviderDTO = {
	id: string;
	name: string;
	about: string;
	credentials: string[];
	photo_url: string | null;
};

const mapProviderDTO = (dto: ProviderDTO): Provider => ({
	id: dto.id,
	name: dto.name,
	about: dto.about,
	credentials: dto.credentials,
	photoUrl: dto.photo_url,
});

type CareServiceDTO = {
	id: string;
	name: string;
	description: string;
	old_service_id: string;
	x12_codes: string[];
	states: string[];
	created_at: string;
	updated_at: string;
	deleted_at: string;
};

const mapCareServiceFromDTO = (dto: CareServiceDTO): CareService => ({
	id: dto.id,
	name: dto.name,
	description: dto.description,
	oldServiceID: dto.old_service_id,
	x12Codes: dto.x12_codes,
	states: dto.states,
	createdAt: DateTime.fromISO(dto.created_at),
	updatedAt: DateTime.fromISO(dto.updated_at),
	deletedAt: dto.deleted_at ? DateTime.fromISO(dto.deleted_at) : undefined,
});
